/**
 * Szezon-réteg — EGYETLEN forrás a négy évszak tartalmához.
 *
 * Ezt importálja:
 *  - a Vite build (vite.config.ts → transformIndexHtml): a statikus hero-héj szövege/képe és a
 *    FAQPage JSON-LD szezonális kérdései build-időben kerülnek az index.html-be,
 *  - a React (src/config/season.ts → SeasonSection, Faq, Hero): ugyanazt a szezont rendereli,
 *    így a héj és a React első képkockája pixelre egyezik.
 *
 * A szezon build-időben dől el (vagy a VITE_SEASON env-ből), NEM a böngészőben — a crawler és a
 * social-scraper is a kész HTML-t látja. A négy váltónapon a .github/workflows/season-redeploy.yml
 * indít új buildet a Vercel Deploy Hookkal.
 *
 * TÉNY-SZABÁLY: minden állítás vagy a meglévő oldal tartalmából jön (felszereltség, távolságok, házirend),
 * vagy az ügyfél megerősítette (parkolás hóban), vagy nyilvános, ellenőrzött forrás (advent, Bazilika, Duna–Ipoly NP).
 * Ami nincs megerősítve, az NEM kerül ide. A nyitott kérdések és a forráslista: docs/SZEZON-TENYEK-Peternek.md.
 * ⚠️ Élesítés előtt megerősítendő: „télen is nyitva tartó” (téli lead) — ha van zárva tartás, át kell írni.
 *
 * FONTOS: a hero-szövegek karakterei a public/fonts/*-hero.woff2 részhalmazban vannak. Ha ezeken a
 * szövegeken változtatsz, futtasd: `npm run fonts`.
 */

export type SeasonKey = 'spring' | 'summer' | 'autumn' | 'winter';
export type Lang = 'hu' | 'en';

export const SEASON_ORDER: SeasonKey[] = ['spring', 'summer', 'autumn', 'winter'];

/** Váltónapok (hónap, nap) — inkluzív kezdet. */
export const SEASON_STARTS: Record<SeasonKey, [number, number]> = {
  spring: [3, 1],
  summer: [6, 1],
  autumn: [9, 15],
  winter: [12, 1],
};

export function getSeason(date: Date = new Date()): SeasonKey {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const after = (key: SeasonKey) => {
    const [sm, sd] = SEASON_STARTS[key];
    return m > sm || (m === sm && d >= sd);
  };
  if (after('winter')) return 'winter';
  if (after('autumn')) return 'autumn';
  if (after('summer')) return 'summer';
  if (after('spring')) return 'spring';
  return 'winter'; // január–február
}

export function isSeasonKey(v: unknown): v is SeasonKey {
  return typeof v === 'string' && (SEASON_ORDER as string[]).includes(v);
}

export type SeasonExperience = {
  title: string;
  /**
   * Rövid, TÉNYSZERŰ címke a bal hasábba: meddig tart / hol van. A korábbi 01/02/03 sorszám
   * helyén áll — az csak díszítés volt, a darabszámot pedig már a szekció-címke kimondja.
   * Mindig a leírásból jön, és onnan ilyenkor kivesszük (egy információ, egy helyen).
   */
  meta: string;
  desc: string;
  /** Külső forrás (GEO: citálható entitás). */
  href?: string;
  /** Csak akkor mutatjuk, ha a dátum-ablakban vagyunk (pl. advent). ISO hónap-nap. */
  window?: { from: string; to: string };
};

export type SeasonFaq = {
  q: string;
  a: string;
  /** Opcionális felsorolás a válasz után (a JSON-LD-be a szöveg mögé fűzve kerül). */
  items?: string[];
};

export type SeasonContent = {
  /** Hero alcím — RÖVID (≤ 60 karakter), a hero-részhalmaz betűvel. */
  heroSubtitle: string;
  /** Hero kép alapneve (public/images/<base>.avif + responsive/<base>-<w>.avif). */
  heroImage: string;
  heroAlt: string;
  label: string;
  heading: string;
  headingItalic: string;
  /** Definíciós lead: „A haaziko télen …” — egy AI-válaszban önállóan idézhető mondattal indul. */
  lead: string;
  body: string;
  experiencesHeading: string;
  experiences: SeasonExperience[];
  /**
   * Két kép a szekció aljára (diptichon). Csak akkor van, ha VAN rá valódi, az évszakot nem
   * meghazudtoló anyag — ősszel Péter 2026-09-22-i jacuzzi-fotói. A többi évszak a saját
   * fotózásáig kép nélkül marad: inkább nincs kép, mint májusi zöld lomb a téli szekcióban.
   */
  sectionImages?: { base: string; alt: string }[];
  faq: SeasonFaq[];
};

/**
 * Hero-képek. Mind a 2025. májusi fotózásból (őszi/téli anyag még nincs — az ügyféltől kértük), ezért
 * az alt-szöveg és a kép NEM állít évszakot: ősszel a meleg fa-belső, télen az éjszaka kivilágított ház.
 */
const IMG = {
  spring: { base: 'ahaaziko-0521-174926', alt: { hu: 'Haaziko alpesi faház a zöld domboldalon, délutáni fényben', en: 'Haaziko A-frame chalet on the green hillside in afternoon light' } },
  summer: { base: 'ahaaziko-0521-122149', alt: { hu: 'Haaziko terasz — panorámás kilátás a Pilis hegyeire', en: 'Haaziko terrace — panoramic view of the Pilis hills' } },
  autumn: { base: 'ahaaziko-0521-215745', alt: { hu: 'Haaziko belső tere — fa gerendák, meleg fény, kanapé az ablakfal előtt', en: 'Inside haaziko — timber beams, warm light, sofa in front of the glass wall' } },
  winter: { base: 'ahaaziko-0521-213923', alt: { hu: 'Haaziko éjszaka — kivilágított faház az erdő szélén', en: 'Haaziko at night — lit-up chalet at the forest edge' } },
} as const;

const LINKS = {
  dunaIpoly: 'https://www.dunaipoly.hu/',
  bazilika: 'https://www.bazilika-esztergom.hu/',
  advent: 'https://visitesztergom.hu/esztergomi-adventi-vasar/',
  aquasziget: 'https://www.aquasziget.hu/',
};

export const SEASONS: Record<Lang, Record<SeasonKey, SeasonContent>> = {
  hu: {
    spring: {
      heroSubtitle: 'Jacuzzi, friss zöld, forrástúrák a Pilisben.',
      heroImage: IMG.spring.base,
      heroAlt: IMG.spring.alt.hu,
      label: 'Tavasz a haazikóban',
      heading: 'Amikor az erdő',
      headingItalic: 'újraindul.',
      lead:
        'A haaziko tavasszal a legzöldebb: a közel 4000 m²-es ősfás telek márciustól májusig hétről hétre változik, és a 80 m²-es teraszról a Pilis ébredését nézed. A ház Esztergom szélén, az erdő határán áll, Budapestről egy órán belül elérhető.',
      body:
        'Reggel a madarak keltenek, napközben a jelzett túrautak indulnak közvetlenül a ház mellől, este a jacuzzi és a tábortűz jön. A hűvös tavaszi estékre a ház padlófűtéses.',
      experiencesHeading: 'Három tavaszi program a környéken',
      experiences: [
        { title: 'Holop-forrás és a Pilis jelzett útjai', meta: 'Séta a háztól', desc: 'A ház közeléből induló túraútvonalak — tavasszal a legzöldebbek.', href: LINKS.dunaIpoly },
        { title: 'Esztergomi Bazilika és a Várhegy', meta: 'Pár perc autóval', desc: 'A kupolakilátóból a Dunakanyar és a Pilis egyszerre látszik.', href: LINKS.bazilika },
        { title: 'Dobogókő', meta: 'Fél nap', desc: 'A Pilis legmagasabb pontja, jelzett utakkal minden szintre.' },
      ],
      faq: [
        { q: 'Milyen a haaziko tavasszal?', a: 'Csendes, zöld és madárcsicsergős. A jacuzzi, a tábortűz és a terasz már használható, a ház padlófűtéses, így a hűvös tavaszi estéken is meleg.' },
        { q: 'Húsvétra és a májusi hosszú hétvégékre lehet foglalni?', a: 'Igen, közvetlenül a weboldalon — a naptárban látszanak a szabad napok.' },
      ],
    },

    summer: {
      heroSubtitle: 'Jacuzzi, 80 m² napozóterasz, tábortűz a kertben.',
      heroImage: IMG.summer.base,
      heroAlt: IMG.summer.alt.hu,
      label: 'Nyár a haazikóban',
      heading: 'Terasz, árnyék,',
      headingItalic: 'csillagos ég.',
      lead:
        'A haaziko nyáron árnyékos: az ősfás telek fái a legmelegebb napokon is hűvöset adnak, a 80 m²-es sarokterasz reggel kávézóhely, délután napozóterasz, este a jacuzzi és a tábortűz jön. A ház klímás, Esztergom szélén, Budapestről egy órán belül.',
      body:
        'Nyáron a kert a főszereplő: grillezés, bográcsozás, marshmallow a tűz fölött, és a csillagos ég, amit a városban nem látni. A Bazilika és Esztergom belvárosa autóval pár perc, a Pilis jelzett útjai a ház mellől indulnak.',
      experiencesHeading: 'Három nyári program a környéken',
      experiences: [
        { title: 'Jacuzzi, tábortűz és bográcsozás', meta: 'A kertben', desc: 'Kültéri jacuzzi a fa teraszon, saját tábortűzrakó és grillező hely az ősfás kertben — az esti program adott.' },
        { title: 'Esztergomi Bazilika és a Duna-part', meta: 'Pár perc autóval', desc: 'A Bazilika, a Duna-part és a Mária Valéria hídon át Párkány.', href: LINKS.bazilika },
        { title: 'Erdei túrák a Pilisben', meta: 'Fél nap', desc: 'A ház mellől induló jelzett utakon az erdő délben is hűvös; Dobogókő és a Kőhegy is elérhető.', href: LINKS.dunaIpoly },
      ],
      faq: [
        { q: 'Van klíma a haazikóban?', a: 'Igen, a ház splitklímás, és az ősfás telek árnyéka miatt a legmelegebb napokon is kellemes.' },
        { q: 'Lehet nyáron tábortüzet rakni?', a: 'Igen, a kertben kijelölt tábortűzrakó helyen — kivéve az országos tűzgyújtási tilalom idején.' },
      ],
    },

    autumn: {
      heroSubtitle: 'Lombszínek a Pilisben, jacuzzi és tábortűz.',
      heroImage: IMG.autumn.base,
      heroAlt: IMG.autumn.alt.hu,
      label: 'Ősz a haazikóban',
      heading: 'Az erdő színt vált,',
      headingItalic: 'te lelassulsz.',
      lead:
        'A haaziko ősszel a leglátványosabb: az ősfás telek és a Pilis szemközti oldala októberben színt vált, és ezt a padlótól plafonig érő ablakfal mögül, padlófűtött házból nézed. Esztergom szélén, az erdő határán, Budapestről egy órán belül — az őszi szünet és a novemberi hosszú hétvégék kézenfekvő kiugrása.',
      body:
        'Ősszel a nap rövidebb, a program egyszerűbb: délelőtt túra a lombok között, délután jacuzzi és tábortűz, este Netflix a nagy ablak előtt. A ház önálló bejutással várja az érkezőket.',
      experiencesHeading: 'Három őszi program a környéken',
      sectionImages: [
        { base: 'ahaaziko-jacuzzi-terasz', alt: 'A haaziko kültéri jacuzzija a fa teraszon, háttérben az A-frame faház' },
        { base: 'ahaaziko-jacuzzi-kert', alt: 'A jacuzzi a teraszon, körülötte ősfák és füzérfények' },
      ],
      experiences: [
        { title: 'Lombszínek a Pilisben', meta: 'Fél nap', desc: 'A ház mellől induló jelzett utak októberben a legszebbek; a Kőhegy és Dobogókő a Dunakanyarra néz.', href: LINKS.dunaIpoly },
        { title: 'Esztergomi Bazilika és Belváros', meta: 'Pár perc autóval', desc: 'Múzeumok, kávézók, a Várhegy — esős napra is program.', href: LINKS.bazilika },
        { title: 'Kültéri jacuzzi és tábortűz', meta: 'A teraszon', desc: 'A hűvös őszi estékre ez a legjobb program: jacuzzi a fa teraszon, majd tűz a kijelölt tűzrakóhelyen.' },
      ],
      faq: [
        { q: 'Fűtött a haaziko ősszel?', a: 'Igen, a házban padlófűtés és fűtő klíma is van, a hűvös őszi estéken is meleg. A vendégek érkezésére a házat előre befűtjük.' },
        { q: 'Lehet ősszel tábortüzet rakni?', a: 'Igen, a kertben kijelölt tábortűzrakó hely áll rendelkezésre, ősszel is. A tűzrakóhely biztonságos és szélvédett, a tűzifa a helyszínen megtalálható.' },
        { q: 'Mit lehet csinálni a haaziko környékén ősszel?', a: 'Túrázni a Pilis lombszínei között (a jelzett utak a ház mellől indulnak), Esztergom belvárosába és a Bazilikához autóval pár perc, Dobogókő is elérhető. Esős napra jacuzzi, tábortűz és Netflix.' },
      ],
    },

    winter: {
      heroSubtitle: 'Jacuzzi és tábortűz a havas erdő szélén.',
      heroImage: IMG.winter.base,
      heroAlt: IMG.winter.alt.hu,
      label: 'Tél a haazikóban',
      heading: 'Kint hó,',
      headingItalic: 'bent meleg.',
      lead:
        'A haaziko télen is nyitva tartó, padlófűtéses alpesi faház Esztergom szélén, az erdő határán, Budapestről egy órán belül. A padlótól plafonig érő ablakfal az erdőre néz, a jacuzzi és a tábortűz télen is működik, a házhoz vezető utat rendszeresen takarítják.',
      body:
        'A téli haaziko a bekuckózásról szól: padlófűtés és fűtő klíma, érkezésre befűtött ház, önálló bejutás, a nagy ablak előtt Netflix. A vendégek kedvence a tábortűz, a jacuzzi és az adventi vásár hármasa — tűzifa a helyszínen van. Esztergom adventi vására november végétől karácsony előttig tart, a Bazilika és a Belváros autóval pár perc. Az ünnepek között és szilveszterkor is foglalható, ezekre érdemes időben lefoglalni.',
      experiencesHeading: 'Téli programok a környéken',
      experiences: [
        { title: 'Esztergomi adventi vásár', meta: 'Pár perc autóval', desc: 'Forralt bor, kézművesek és fények a Széchenyi téren, az adventi hétvégéken (péntektől vasárnapig).', href: LINKS.advent, window: { from: '11-15', to: '12-24' } },
        { title: 'Havas túra Dobogókőre', meta: 'Fél nap', desc: 'A ház mellől induló jelzett utak télen is járhatók; Dobogókő kilátója, meleg tea a fenti menedékházban.', href: LINKS.dunaIpoly },
        { title: 'Aquasziget Esztergom', meta: 'Pár perc autóval', desc: 'Meleg medencék és szaunák a hideg napokra.', href: LINKS.aquasziget },
      ],
      faq: [
        {
          q: 'Fűtött a ház télen?',
          a: 'Igen, a haaziko télen teljesen fűtött, így hideg időben is kényelmes és meleg. A faházban modern padlófűtés és fűtő klíma is működik. A vendégek érkezésére a házat előre befűtjük, így már az első perctől kellemes a hőérzet.',
        },
        {
          q: 'Lehet télen is tábortüzet rakni?',
          a: 'Igen, télen is lehet tábortüzet rakni a kijelölt tűzrakóhelyen. A tűzrakóhely biztonságos és szélvédett, a tűzifa a helyszínen rendelkezésre áll.',
        },
        {
          q: 'Járható az út hóban?',
          a: 'Igen, a hozzánk vezető út hóban is járható: rendszeresen takarítják, és a környék infrastruktúrája jó, így a vendégek télen is gond nélkül megközelítik a házat. Nagyon erős havazás esetén előfordulhat, hogy a telek alján található lenti parkolóban kell megállni, és onnan felsétálni a házhoz — az autó ilyenkor is a zárható kapuval védett telken parkol.',
        },
        {
          q: 'Milyen programok vannak a környéken decemberben?',
          a: 'Decemberben Esztergom és a környék kifejezetten hangulatos, sok téli programmal. A haaziko vendégei általában a tábortűz, a jacuzzi és az adventi vásár hármasát szeretik a legjobban.',
          items: [
            'Esztergomi Adventi Vásár — forralt bor, kézművesek, fények',
            'A Bazilika karácsonyi koncertjei — ünnepi hangulat, klasszikus zene',
            'Dunakanyar téli panorámája — séta a Prímás-szigeten',
            'Dobogókő — havas túrák, kilátó, meleg tea a fenti menedékházban',
            'Visegrádi vár — téli kirándulás, kevés turistával',
            'Aquasziget Esztergom — meleg medencék, szaunák',
            'Jégpálya Esztergomban — szezonális korcsolyázás',
          ],
        },
      ],
    },
  },

  en: {
    spring: {
      heroSubtitle: 'Jacuzzi, fresh green, spring trails in the Pilis.',
      heroImage: IMG.spring.base,
      heroAlt: IMG.spring.alt.en,
      label: 'Spring at haaziko',
      heading: 'When the forest',
      headingItalic: 'wakes up.',
      lead:
        'Spring is when haaziko is greenest: the nearly 4,000 m² wooded plot changes week by week from March to May, and from the 80 m² terrace you watch the Pilis hills come back to life. The A-frame chalet sits on the edge of Esztergom, right at the forest line, within an hour of Budapest.',
      body:
        'Birds wake you, marked trails start right by the house, and the day ends in the jacuzzi and at the fire pit. For cool spring evenings the house has underfloor heating.',
      experiencesHeading: 'Three spring outings nearby',
      experiences: [
        { title: 'Holop spring and the marked trails of the Pilis', meta: 'Walk from the house', desc: 'Hiking trails start near the house — greenest in spring.', href: LINKS.dunaIpoly },
        { title: 'Esztergom Basilica and Castle Hill', meta: 'Minutes by car', desc: 'From the dome lookout you see the Danube Bend and the Pilis at once.', href: LINKS.bazilika },
        { title: 'Dobogókő', meta: 'Half a day', desc: 'The highest point of the Pilis, with marked trails for every level.' },
      ],
      faq: [
        { q: 'What is haaziko like in spring?', a: 'Quiet, green and full of birdsong. The jacuzzi, the fire pit and the terrace are already in use, and the house has underfloor heating for cool spring evenings.' },
        { q: 'Can I book for Easter or the May long weekends?', a: 'Yes, directly on this website — the calendar shows the free dates.' },
      ],
    },

    summer: {
      heroSubtitle: 'Jacuzzi, 80 m² sun terrace, fire pit in the garden.',
      heroImage: IMG.summer.base,
      heroAlt: IMG.summer.alt.en,
      label: 'Summer at haaziko',
      heading: 'Terrace, shade,',
      headingItalic: 'starry sky.',
      lead:
        'haaziko in summer is all shade: the old trees keep the plot cool on the hottest days, and the 80 m² corner terrace is a coffee spot in the morning, a sun deck in the afternoon, then the jacuzzi and the fire pit take over at night. The A-frame chalet is air-conditioned, on the edge of Esztergom, within an hour of Budapest.',
      body:
        'In summer the garden takes over: barbecue, cauldron cooking, marshmallows over the fire, and a starry sky you never see in the city. The Basilica and Esztergom old town are minutes by car; the marked trails of the Pilis start by the house.',
      experiencesHeading: 'Three summer outings nearby',
      experiences: [
        { title: 'Jacuzzi, bonfire and cauldron cooking', meta: 'In the garden', desc: 'The outdoor jacuzzi on the wooden deck, your own fire pit and grill under old trees — the evening plan takes care of itself.' },
        { title: 'Esztergom Basilica and the Danube bank', meta: 'Minutes by car', desc: 'The Basilica, the riverbank and Štúrovo across the Mária Valéria bridge.', href: LINKS.bazilika },
        { title: 'Forest hikes in the Pilis', meta: 'Half a day', desc: 'Marked trails from the house stay cool at noon; Dobogókő and Kőhegy are both within reach.', href: LINKS.dunaIpoly },
      ],
      faq: [
        { q: 'Is there air conditioning at haaziko?', a: 'Yes, the house has split air conditioning, and the shade of the old trees keeps it pleasant even on the hottest days.' },
        { q: 'Can we have a bonfire in summer?', a: 'Yes, at the designated fire pit in the garden — except during a national fire ban.' },
      ],
    },

    autumn: {
      heroSubtitle: 'Autumn colours in the Pilis, jacuzzi and a fire.',
      heroImage: IMG.autumn.base,
      heroAlt: IMG.autumn.alt.en,
      label: 'Autumn at haaziko',
      heading: 'The forest changes colour,',
      headingItalic: 'you slow down.',
      lead:
        'Autumn is when haaziko looks its best: the wooded plot and the facing slope of the Pilis turn colour in October, and you watch it from behind a floor-to-ceiling glass wall in a house with underfloor heating. On the edge of Esztergom, at the forest line, within an hour of Budapest — the obvious escape for autumn break and November long weekends.',
      body:
        'Autumn days are shorter and the plan is simpler: a morning hike among the leaves, an afternoon in the jacuzzi and by the fire, Netflix in front of the big window at night. Self check-in is available.',
      experiencesHeading: 'Three autumn outings nearby',
      sectionImages: [
        { base: 'ahaaziko-jacuzzi-terasz', alt: 'The outdoor jacuzzi on the wooden deck, the A-frame chalet behind it' },
        { base: 'ahaaziko-jacuzzi-kert', alt: 'The jacuzzi on the deck, surrounded by old trees and string lights' },
      ],
      experiences: [
        { title: 'Autumn colours in the Pilis', meta: 'Half a day', desc: 'The marked trails from the house peak in October; Kőhegy and Dobogókő look out over the Danube Bend.', href: LINKS.dunaIpoly },
        { title: 'Esztergom Basilica and old town', meta: 'Minutes by car', desc: 'Museums, cafés, Castle Hill — a plan for rainy days too.', href: LINKS.bazilika },
        { title: 'Outdoor jacuzzi and a fire', meta: 'On the deck', desc: 'The best plan for a cool autumn evening: the jacuzzi on the wooden deck, then a fire at the designated pit.' },
      ],
      faq: [
        { q: 'Is haaziko heated in autumn?', a: 'Yes. The house has underfloor heating and a heat-pump air conditioner, warm on cool autumn evenings. We heat the house before you arrive.' },
        { q: 'Can we have a bonfire in autumn?', a: 'Yes, the garden has a designated fire pit that is in use in autumn as well; firewood is provided on site.' },
        { q: 'What is there to do around haaziko in autumn?', a: 'Hike among the autumn colours of the Pilis (marked trails start by the house), drive a few minutes to Esztergom old town and the Basilica, or up to Dobogókő. On rainy days: the jacuzzi, a fire in the garden and Netflix.' },
      ],
    },

    winter: {
      heroSubtitle: 'Jacuzzi and a fire pit at the snowy forest edge.',
      heroImage: IMG.winter.base,
      heroAlt: IMG.winter.alt.en,
      label: 'Winter at haaziko',
      heading: 'Snow outside,',
      headingItalic: 'warm inside.',
      lead:
        'haaziko is an A-frame chalet open all winter, with underfloor heating, on the edge of Esztergom at the forest line, within an hour of Budapest. The floor-to-ceiling glass wall faces the forest, the jacuzzi and the fire pit work in winter too, and the road to the house is cleared regularly.',
      body:
        'Winter at haaziko is about hunkering down: underfloor heating and a heat-pump air conditioner, the house warmed up before you arrive, self check-in, Netflix in front of the big window. Guests love the fire pit, the jacuzzi and the Advent market combination — firewood is provided on site. Esztergom Advent market runs from late November until just before Christmas; the Basilica and old town are minutes by car. The house is available between Christmas and New Year as well; book early for those dates.',
      experiencesHeading: 'Winter outings nearby',
      experiences: [
        { title: 'Esztergom Advent market', meta: 'Minutes by car', desc: 'Mulled wine, craft stalls and lights on Széchenyi square, on Advent weekends (Friday to Sunday).', href: LINKS.advent, window: { from: '11-15', to: '12-24' } },
        { title: 'Snow hike to Dobogókő', meta: 'Half a day', desc: 'The marked trails from the house are walkable in winter; the Dobogókő lookout and hot tea at the mountain lodge.', href: LINKS.dunaIpoly },
        { title: 'Aquasziget Esztergom', meta: 'Minutes by car', desc: 'Warm pools and saunas for cold days.', href: LINKS.aquasziget },
      ],
      faq: [
        {
          q: 'Is the house heated in winter?',
          a: 'Yes, haaziko is fully heated in winter, so it stays comfortable and warm in cold weather. The chalet has modern underfloor heating and a heat-pump air conditioner. We warm the house up before you arrive, so it is pleasant from the first minute.',
        },
        {
          q: 'Can we have a bonfire in winter too?',
          a: 'Yes, you can light a fire at the designated fire pit in winter as well. The pit is safe and sheltered from the wind, and firewood is provided on site.',
        },
        {
          q: 'Is the road passable in snow?',
          a: 'Yes, the road to us is passable in snow: it is cleared regularly and the local infrastructure is good, so guests reach the house without trouble in winter. After very heavy snowfall you may need to park in the lower car park at the bottom of the plot and walk up to the house — even then the car stays on the plot behind a lockable gate.',
        },
        {
          q: 'What is there to do nearby in December?',
          a: 'December is especially atmospheric in Esztergom and the surrounding area, with plenty of winter programmes. haaziko guests usually love the combination of the fire pit, the jacuzzi and the Advent market.',
          items: [
            'Esztergom Advent market — mulled wine, craft stalls, lights',
            'Christmas concerts at the Basilica — classical music, festive mood',
            'Winter panorama of the Danube Bend — a walk on Prímás Island',
            'Dobogókő — snow hikes, lookout, hot tea at the mountain lodge',
            'Visegrád castle — a winter trip with few tourists',
            'Aquasziget Esztergom — warm pools and saunas',
            'Ice rink in Esztergom — seasonal skating',
          ],
        },
      ],
    },
  },
};

/** Az összes hero-alcím mindkét nyelven (a betű-részhalmaz generálásához). */
export function allHeroTexts(): string[] {
  const out: string[] = [];
  for (const lang of ['hu', 'en'] as Lang[]) {
    for (const key of SEASON_ORDER) out.push(SEASONS[lang][key].heroSubtitle);
  }
  return out;
}

/** Egy program látható-e most (dátum-ablak, ha van). */
export function experienceVisible(e: SeasonExperience, date: Date = new Date()): boolean {
  if (!e.window) return true;
  const md = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return md >= e.window.from && md <= e.window.to;
}
